import { useCallback, useEffect, useRef } from 'react';

const PULL_THRESHOLD = 72;
const MAX_PULL = 120;
const RESISTANCE = 0.5;
const SCROLL_TOP_TOLERANCE = 1;
const TOUCH_SLOP = 8;

type UsePullToRefreshArgs = {
  onRefresh: () => void | Promise<unknown>;
  isRefreshing?: boolean;
  enabled?: boolean;
};

type UsePullToRefreshResult = {
  setContainerElement: (element: HTMLElement | null) => void;
  setContentElement: (element: HTMLElement | null) => void;
  setIndicatorElement: (element: HTMLElement | null) => void;
  setSpinnerElement: (element: HTMLElement | null) => void;
};

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element);

  return (
    (overflowY === 'auto' || overflowY === 'scroll') &&
    element.scrollHeight > element.clientHeight
  );
};

/**
 * Walks up from the touched node to find the scroller that the gesture would
 * actually scroll, so a pull only starts when that scroller is already at its top.
 */
const findScrollableWithin = (
  target: EventTarget | null,
  container: HTMLElement,
): HTMLElement | null => {
  let current: HTMLElement | null = null;

  if (target instanceof HTMLElement) {
    current = target;
  } else if (target instanceof Node) {
    current = target.parentElement;
  }

  while (current && current !== container) {
    if (isScrollable(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return isScrollable(container) ? container : null;
};

/**
 * Pull-to-refresh gesture driven entirely through direct DOM writes. Re-rendering
 * mid-gesture would remount the touched element (virtualized lists swap their
 * subtree), which makes the browser cancel the touch, so no React state is used
 * while the finger is down.
 */
const usePullToRefresh = ({
  onRefresh,
  isRefreshing = false,
  enabled = true,
}: UsePullToRefreshArgs): UsePullToRefreshResult => {
  const containerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);
  const indicatorRef = useRef<HTMLElement | null>(null);
  const spinnerRef = useRef<HTMLElement | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const isRefreshingRef = useRef(isRefreshing);

  onRefreshRef.current = onRefresh;
  isRefreshingRef.current = isRefreshing;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let startY = 0;
    let distance = 0;
    let isTracking = false;
    let isPulling = false;

    const paint = (next: number) => {
      distance = next;

      const content = contentRef.current;
      if (content) {
        content.style.transform =
          next > 0 ? `translate3d(0, ${next}px, 0)` : '';
      }

      const indicator = indicatorRef.current;
      if (indicator) {
        indicator.style.height = `${Math.max(next, 0)}px`;
      }

      const spinner = spinnerRef.current;
      if (spinner) {
        const progress = Math.min(next / PULL_THRESHOLD, 1);
        spinner.style.opacity = next > 0 ? `${0.3 + progress * 0.7}` : '0';

        if (next >= PULL_THRESHOLD) {
          spinner.dataset.spinning = 'true';
          spinner.style.transform = '';
        } else {
          delete spinner.dataset.spinning;
          spinner.style.transform = `rotate(${progress * 270}deg)`;
        }
      }
    };

    const setAnimated = (animated: boolean) => {
      const transition = animated ? '0.25s ease-out' : '';

      const content = contentRef.current;
      if (content) {
        content.style.transition = animated ? `transform ${transition}` : '';
      }

      const indicator = indicatorRef.current;
      if (indicator) {
        indicator.style.transition = animated ? `height ${transition}` : '';
      }
    };

    const alignIndicator = () => {
      const indicator = indicatorRef.current;
      const container = containerRef.current;

      if (indicator && container) {
        indicator.style.top = `${container.getBoundingClientRect().top}px`;
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isTracking) {
        return;
      }

      const deltaY = event.touches[0].clientY - startY;

      if (!isPulling) {
        if (deltaY <= 0) {
          if (-deltaY > TOUCH_SLOP) {
            stopTracking();
          }
          return;
        }

        // Claim the gesture on the first downward move; iOS ignores
        // preventDefault once it has already started scrolling.
        if (event.cancelable) {
          event.preventDefault();
        }

        if (deltaY < TOUCH_SLOP) {
          return;
        }

        isPulling = true;
        setAnimated(false);
      }

      if (deltaY <= 0) {
        paint(0);
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      paint(Math.min((deltaY - TOUCH_SLOP) * RESISTANCE, MAX_PULL));
    };

    const handleTouchEnd = () => {
      if (!isTracking) {
        return;
      }

      const wasPulling = isPulling;
      const pulledDistance = distance;

      stopTracking();
      setAnimated(true);

      if (
        wasPulling &&
        pulledDistance >= PULL_THRESHOLD &&
        !isRefreshingRef.current
      ) {
        paint(PULL_THRESHOLD);
        void Promise.resolve(onRefreshRef.current()).finally(() => paint(0));
        return;
      }

      paint(0);
    };

    function stopTracking() {
      isTracking = false;
      isPulling = false;
      document.removeEventListener('touchmove', handleTouchMove, true);
      document.removeEventListener('touchend', handleTouchEnd, true);
      document.removeEventListener('touchcancel', handleTouchEnd, true);
    }

    const handleTouchStart = (event: TouchEvent) => {
      const container = containerRef.current;

      if (isTracking || isRefreshingRef.current || !container) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Node) || !container.contains(target)) {
        return;
      }

      const scroller = findScrollableWithin(target, container);
      if (scroller && scroller.scrollTop > SCROLL_TOP_TOLERANCE) {
        return;
      }

      startY = event.touches[0].clientY;
      isTracking = true;
      isPulling = false;
      alignIndicator();

      document.addEventListener('touchmove', handleTouchMove, {
        capture: true,
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd, { capture: true });
      document.addEventListener('touchcancel', handleTouchEnd, {
        capture: true,
      });
    };

    document.addEventListener('touchstart', handleTouchStart, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, true);
      stopTracking();
      paint(0);
    };
  }, [enabled]);

  const setContainerElement = useCallback((element: HTMLElement | null) => {
    containerRef.current = element;
  }, []);

  const setContentElement = useCallback((element: HTMLElement | null) => {
    contentRef.current = element;
  }, []);

  const setIndicatorElement = useCallback((element: HTMLElement | null) => {
    indicatorRef.current = element;
  }, []);

  const setSpinnerElement = useCallback((element: HTMLElement | null) => {
    spinnerRef.current = element;
  }, []);

  return {
    setContainerElement,
    setContentElement,
    setIndicatorElement,
    setSpinnerElement,
  };
};

export { PULL_THRESHOLD, findScrollableWithin, isScrollable, usePullToRefresh };
export type { UsePullToRefreshArgs, UsePullToRefreshResult };
